import { useEffect, useState, useRef } from "react";
import "../styles/Banner.css";
import banner1 from "../assets/vv.mp4";
import banner2 from "../assets/bannernew2.png";
import banner3 from "../assets/bannernew1.png";

export default function Banner() {
  const images = [banner1, banner2, banner3];
  const [current, setCurrent] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const intervalRef = useRef(null);

  // ── Auto slide with pause on hover ────────────────────────
  useEffect(() => {
    if (!isAutoPlay) return;

    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(intervalRef.current);
  }, [isAutoPlay, images.length]);

  const nextSlide = () => {
    setCurrent((prev) => (prev + 1) % images.length);
    // Reset autoplay timer
    setIsAutoPlay(false);
    setTimeout(() => setIsAutoPlay(true), 500);
  };

  const prevSlide = () => {
    setCurrent((prev) => (prev - 1 + images.length) % images.length);
    // Reset autoplay timer
    setIsAutoPlay(false);
    setTimeout(() => setIsAutoPlay(true), 500);
  };

  // ── Swipe detection (mobile) ────────────────────────────────
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;

    const diffX = touchStartX.current - touchEndX;
    const diffY = touchStartY.current - touchEndY;

    // Only trigger if horizontal swipe is dominant
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        nextSlide(); // Swipe left
      } else {
        prevSlide(); // Swipe right
      }
    }
  };

  // ── Pause autoplay on mouse enter ────────────────────────────
  const handleMouseEnter = () => {
    setIsAutoPlay(false);
  };

  const handleMouseLeave = () => {
    setIsAutoPlay(true);
  };

  return (
    <div
      className="banner"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="region"
      aria-label="Banner carousel"
    >
      {/* ── Images Container ──────────────────────────────────── */}
      <div className="banner-slides">
        {images.map((item, index) =>
          item.endsWith(".mp4") ? (
            <video
              key={index}
              src={item}
              className={`banner-img ${index === current ? "active" : ""}`}
              autoPlay={index === current}
              muted
              loop
              playsInline
              aria-hidden={index !== current}
            />
          ) : (
            <img
              key={index}
              src={item}
              alt={`Banner slide ${index + 1}`}
              className={`banner-img ${index === current ? "active" : ""}`}
              aria-hidden={index !== current}
            />
          )
        )}
      </div>

      {/* ── Navigation Arrows ────────────────────────────────── */}
      <button
        className="banner-arrow banner-arrow--left"
        onClick={prevSlide}
        aria-label="Previous slide"
        title="Previous slide"
      >
        <span>❮</span>
      </button>
      <button
        className="banner-arrow banner-arrow--right"
        onClick={nextSlide}
        aria-label="Next slide"
        title="Next slide"
      >
        <span>❯</span>
      </button>

      {/* ── Dots Navigation ───────────────────────────────────── */}
      <div className="banner-dots" role="tablist" aria-label="Slide navigation">
        {images.map((_, index) => (
          <button
            key={index}
            className={`banner-dot ${index === current ? "banner-dot--active" : ""}`}
            onClick={() => setCurrent(index)}
            role="tab"
            aria-selected={index === current}
            aria-label={`Go to slide ${index + 1}`}
            title={`Slide ${index + 1}`}
          />
        ))}
      </div>

      {/* ── Slide Counter (optional for UX) ──────────────────── */}
      <div className="banner-counter" aria-live="polite" aria-atomic="true">
        <span>{current + 1}</span> / <span>{images.length}</span>
      </div>
    </div>
  );
}
